import { useMemo } from 'react';
import { formatFt, formatMillioSav } from '../lib/formaz.ts';
import ArSav from '../ui/ArSav.tsx';
import { kozosSkala } from '../ui/arSav.ts';
import { Kartya } from '../ui/elemek.tsx';
import type { LeadSor } from './AdatokPanel.tsx';
import { lepcsoGeometria, levezetesLeadbol, szazalekSzoveg, type Levezetes, type Lepes } from './levezetes.ts';

function Valtozas({ lepes }: { lepes: Lepes }) {
  if (lepes.fajta === 'alapar') return <span className="szam font-medium">{formatFt(lepes.utana)}/m²</span>;
  const szin = lepes.valtozasFt >= 0 ? 'text-kiemelt' : 'text-hiba';
  return (
    <span className={`szam font-medium ${szin}`}>
      {szazalekSzoveg(lepes.valtozasSzazalek)}
      <span className="ml-1.5 font-normal">{lepes.valtozasFt >= 0 ? '+' : '−'}{formatFt(Math.abs(lepes.valtozasFt))}/m²</span>
    </span>
  );
}

function Savok({ l }: { l: Extract<Levezetes, { tipus: 'sav' }> }) {
  const g = useMemo(() => lepcsoGeometria(l.lepesek, l.nyersFtPerM2), [l]);
  const skala = kozosSkala([{ min: l.min, max: l.max }]);
  return (
    <>
      <ol className="mt-3 grid gap-3">
        {l.lepesek.map((lepes, i) => {
          const s = g.lepesek[i]!;
          const szin = lepes.fajta === 'alapar' ? 'bg-vonal-eros' : lepes.valtozasFt >= 0 ? 'bg-kiemelt' : 'bg-hiba';
          return (
            <li key={lepes.kulcs} className="grid gap-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className="min-w-0 break-words text-sm font-medium">{lepes.cimke}</span>
                <Valtozas lepes={lepes} />
              </div>
              <div className="relative h-2 rounded-[3px] bg-felulet" aria-hidden>
                <i className={`absolute inset-y-0 rounded-[3px] ${szin}`} style={{ left: `${s.balSzazalek}%`, width: `${s.szelessegSzazalek}%` }} />
              </div>
              <p className="break-words text-sm text-szoveg-halk">{lepes.reszlet}</p>
            </li>
          );
        })}
        <li className="grid gap-1 border-t border-vonal pt-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className="text-sm font-medium">Ár négyzetméterenként</span>
            <span className="szam font-medium">{formatFt(l.ftPerM2)}/m²</span>
          </div>
          <div className="relative h-2 rounded-[3px] bg-felulet" aria-hidden>
            <i className="absolute inset-y-0 rounded-[3px] bg-masodlagos" style={{ left: `${g.vegso.balSzazalek}%`, width: `${g.vegso.szelessegSzazalek}%` }} />
          </div>
          <p className="text-sm text-szoveg-halk">Ezerre kerekítve.</p>
        </li>
      </ol>

      {l.nemModosit.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium">Megadott, de az árat nem módosító jellemzők</h3>
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-szoveg-halk">
            {l.nemModosit.map((n) => <li key={n.cimke}>{n.cimke}: {n.ertek} (nem módosít)</li>)}
          </ul>
        </div>
      )}

      <div className="mt-4 grid gap-3 border-t border-vonal pt-4">
        <p className="text-sm">
          <span className="szam">{formatFt(l.ftPerM2)}/m²</span> × <span className="szam">{l.alapterulet.toLocaleString('hu-HU')} m²</span> ={' '}
          <b className="szam font-medium">{formatFt(l.kozep)}</b> közép
        </p>
        <p className="text-sm">
          Sáv: <b className="font-medium">±{(l.sav.szelesseg * 100).toLocaleString('hu-HU', { maximumFractionDigits: 1 })}%</b>
        </p>
        <ul className="grid gap-0.5 text-sm text-szoveg-halk">
          {l.sav.indokok.map((i) => <li key={i} className="break-words">{i}</li>)}
        </ul>
        {skala && (
          <ArSav
            min={l.min} max={l.max} kozep={l.kozep} skalaMin={skala.skalaMin} skalaMax={skala.skalaMax}
            felirat={<b className="szam">{formatMillioSav(l.min, l.max)}</b>}
          />
        )}
        <p className="text-sm text-szoveg-halk">{l.kerekitesSzoveg}</p>
      </div>
    </>
  );
}

export default function LevezetesPanel({ lead }: { lead: LeadSor }) {
  const l = useMemo(() => levezetesLeadbol(lead), [lead]);
  if (!l) return null;
  return (
    <Kartya>
      <h2 className="mb-1 text-[17px] font-medium tracking-[-0.015em]">Hogyan jött ki az ár-sáv?</h2>
      <p className="text-szoveg-halk">{l.osszefoglalo}</p>
      {l.tipus === 'szakertoi' ? <p className="mt-3 break-words">{l.magyarazat}</p> : <Savok l={l} />}
    </Kartya>
  );
}
